/**
 * The month a paper was first submitted, read from its arXiv identifier.
 *
 * The OAI `<created>` field cannot answer this: it is observed carrying a
 * recent date for papers whose identifier is years old (2401.08432 arrives
 * with created=2026-08-03), so a revision of last year's paper looked brand
 * new. The identifier is assigned at first submission and never changes on
 * revision, which makes it the one trustworthy signal.
 *
 *  - New style (2007-04 on): `YYMM.NNNN[N]`, optionally with a `vN` suffix.
 *  - Old style: `archive[.SUB]/YYMMNNN`. These run 1991-2007, so a two-digit
 *    year of 91 or more is 19xx.
 */
const NEW_STYLE = /^(\d{2})(\d{2})\.\d{4,5}(?:v\d+)?$/;
const OLD_STYLE = /^[a-z-]+(?:\.[A-Z]{2})?\/(\d{2})(\d{2})\d{3}(?:v\d+)?$/i;

/**
 * The new-style id prefix ("2609") for a "YYYY-MM..." date. Every new-style id
 * submitted in that month or later sorts at or above it as a string.
 */
export function idPrefixFor(date: string): string {
  return `${date.slice(2, 4)}${date.slice(5, 7)}`;
}

/** "YYYY-MM", or null when the identifier is not a recognisable arXiv id. */
export function firstVersionMonth(arxivId: string): string | null {
  const modern = NEW_STYLE.exec(arxivId);
  if (modern) return monthOf(`20${modern[1]}`, modern[2] as string);
  const legacy = OLD_STYLE.exec(arxivId);
  if (legacy) {
    const yy = Number.parseInt(legacy[1] as string, 10);
    return monthOf(`${yy >= 91 ? "19" : "20"}${legacy[1]}`, legacy[2] as string);
  }
  return null;
}

function monthOf(year: string, month: string): string | null {
  const value = Number.parseInt(month, 10);
  if (value < 1 || value > 12) return null;
  return `${year}-${month}`;
}

/**
 * Human dating for a paper, e.g. "first submitted 2024-01 · updated 2026-08-03".
 *
 * `createdDate` is shown as "updated" whenever it falls outside the id's month,
 * because then it is arXiv's re-stamp of a revision, not the submission date.
 */
export function describeDates(arxivId: string, createdDate: string): string {
  const month = firstVersionMonth(arxivId);
  if (month === null || createdDate.startsWith(month)) return `submitted ${createdDate}`;
  return `first submitted ${month} · updated ${createdDate}`;
}
